import React, {FC, ReactElement, useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react";
import {withStripe} from "./WithStripe";
import {billingApi} from "../../backend/BillingApi";
import {selectedCompanyStore} from "../../store/SelectedCompany";
import {BillingStatusDto, isSuccessfulSubscription} from "../../model/billing/BillingStatusDto";
import {HorizontalStepper, StepperStep} from "./HorizontalStepper";
import {Alert, Avatar, Box, Button, CardHeader, Grid, Typography} from "@mui/material";
import {LinkNewDefaultCard} from "./LinkNewDefaultCard";
import moment from "moment";
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LoadingButton from "../../components/button/LoadingButton";
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import {useStripe} from "@stripe/react-stripe-js";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import {notificationStore} from "../../store/NotificationStore";
import {ActiveSubscriptionStatus} from "../../model/billing/ActiveSubscriptionStatusDto";

interface IProps {

}

export const CustomerBillingPlan: FC<IProps> = withStripe(observer((): ReactElement => {
    const [status, setStatus] = useState<BillingStatusDto>();
    const [overrideCard, setOverrideCard] = useState<boolean>(false);

    const cardDetails = status?.cardDetails;

    const refreshStatus = () => {
        if (selectedCompanyStore.isSelected) {
            return billingApi.fetchStatus(selectedCompanyStore.companyId)
                    .then(setStatus)
        }
    }

    const retrySubscriptionPayment = async () => {
        if (selectedCompanyStore.companyId) {
            const result = await billingApi.retrySubscriptionPayment(selectedCompanyStore.companyId)
            if (result.error) {
                notificationStore.addNotification(`Failed to retry payment with error "${result.error}"`, {variant: "error"})
            }
            await refreshStatus();
        }
    }

    useEffect(() => {
        // noinspection JSIgnoredPromiseFromCall
        refreshStatus();
    }, []);

    useEffect(() => {
        if (isSuccessfulSubscription(status?.subscription)) {
            if (selectedCompanyStore.subscriptionStatus !== ActiveSubscriptionStatus.ACTIVE) {
                selectedCompanyStore.refreshSubscriptionStatus()
            }
        }
    }, [status?.subscription]);

    const [initialStep, setInitialStep] = useState<number>(0);

    useEffect(() => {
        let step = 0;
        if (cardDetails) {
            step++;
        }
        if (status?.subscription) {
            step++;
        }
        setInitialStep(step)
    }, [cardDetails, status?.subscription]);

    const stripe = useStripe()!!;


    const onActivateSubscription = async (navigateToNext: () => void) => {
        await billingApi.startSubscription(selectedCompanyStore.companyId);
        await refreshStatus()
        navigateToNext();
    };

    const createCardStep = () => {
        return ({navigateToNext}: { navigateToNext: () => void }) => {
            return (
                    <Box style={{margin: "2rem"}}>
                        <Grid container justifyContent={"center"}>
                            <Grid item>
                                {cardDetails && !overrideCard && <>
                                    <Grid container alignItems={"center"}>
                                        <Grid item>
                                            <Alert>
                                                The card **** ****
                                                ****<b><i>{cardDetails.last4}</i></b>,
                                                exp.<b><i>{moment(new Date(`${cardDetails.expireYear}-${cardDetails.expireMonth}-1`)).format("MM/YY")}{}</i></b> will
                                                be used for the subscription
                                            </Alert>
                                        </Grid>
                                        <Grid item>
                                            <Button startIcon={<AutorenewIcon/>} color={"secondary"}
                                                    variant={"contained"}
                                                    onClick={() => setOverrideCard(true)}>Update</Button>
                                        </Grid>
                                    </Grid>
                                </>}
                                {(!cardDetails || overrideCard) &&
                                        <LinkNewDefaultCard
                                                onSuccess={async () => {
                                                    const subscription = status?.subscription;
                                                    if (subscription && !isSuccessfulSubscription(subscription)) {
                                                        await retrySubscriptionPayment()
                                                    } else {
                                                        await refreshStatus();
                                                    }
                                                    if (!overrideCard) {
                                                        navigateToNext();
                                                    } else {
                                                        setOverrideCard(false)
                                                    }
                                                }}
                                                onCancel={overrideCard ? () => setOverrideCard(false) : undefined}
                                        />
                                }
                            </Grid>
                        </Grid>
                    </Box>
            );
        };
    }

    const steps: StepperStep[] = [
        {
            label: "Card details",
            isComplete: !!cardDetails,
            hasError: () => !cardDetails,
            content: createCardStep()
        },
        {
            label: "Review subscription plan",
            isComplete: !!status?.subscription,
            hasError: () => !status?.plan,
            disabled: () => !status?.cardDetails,
            content: ({navigateToNext}) => {
                const plan = status?.plan;
                return (
                        <Box style={{margin: "2rem"}}>
                            <Grid container justifyContent={"center"}>
                                <Grid item>
                                    {!plan && <>
                                        <Alert severity={"warning"}>Your subscription plan is unknown. Please contact an
                                            accountant</Alert>
                                    </>}
                                    {plan && <>
                                        <Grid container alignItems={"center"}>
                                            <Grid item>
                                                <CardHeader
                                                        avatar={
                                                            <Avatar variant={"rounded"} src={plan.imgUrl}/>
                                                        }
                                                        title={<Typography
                                                                variant={"subtitle1"}>{`${plan.name}`}</Typography>}
                                                        subheader={`${plan.price} ${plan.currency.toUpperCase()}/${plan.recurringInterval}`}
                                                />
                                            </Grid>
                                            {!status?.subscription &&
                                                    <Grid item>
                                                        <LoadingButton startIcon={<PlayArrowIcon/>} color={"primary"}
                                                                       variant={"contained"}
                                                                       onClick={() => onActivateSubscription(navigateToNext)}>Subscribe
                                                            with card</LoadingButton>
                                                    </Grid>
                                            }
                                            {status?.subscription &&
                                                    <Grid item>
                                                        <Alert>Enrolled</Alert>
                                                    </Grid>
                                            }
                                        </Grid>
                                    </>}
                                </Grid>
                            </Grid>
                        </Box>
                );
            }
        },
        {
            label: "Subscription payment",
            isComplete: isSuccessfulSubscription(status?.subscription),
            hasError: () => !isSuccessfulSubscription(status?.subscription),
            disabled: () => !status?.cardDetails || !status?.plan || !status?.subscription,
            content: () => {
                const subscription = status?.subscription;

                const body = () => {
                    if (subscription) {
                        if (isSuccessfulSubscription(subscription)) {
                            return <Grid item>
                                <Alert severity="success">Your subscription is active, nothing to worry about. Next
                                    payment date
                                    is <b><i>{moment(subscription.nextPaymentDate * 1000).format('DD-MMM-YYYY')}</i></b></Alert>
                            </Grid>
                        } else if (subscription.paymentStatus === "requires_action") {
                            return <>
                                <Grid item>
                                    <Alert severity="warning">Subscription payment
                                        for {subscription.amount} {subscription.currency.toUpperCase()} requires
                                        confirmation</Alert>
                                </Grid>
                                <Grid item>
                                    <LoadingButton startIcon={<FlightTakeoffIcon/>} color={"secondary"}
                                                   variant={"contained"} onClick={async () => {
                                        await stripe.confirmCardPayment(subscription.secret);
                                        await refreshStatus();
                                    }
                                    }>Confirm</LoadingButton>
                                </Grid>
                            </>
                        } else {
                            return <>
                                <Grid item container xs={12} justifyContent={"center"} alignItems={"center"}>
                                    <Grid item>
                                        <Alert severity="error">You bank declined the payment
                                            for {subscription.amount} {subscription.currency.toUpperCase()}</Alert>
                                    </Grid>
                                </Grid>
                                <Grid item container xs={12} justifyContent={"center"} alignItems={"center"}
                                      spacing={1}>
                                    <Grid item>
                                        <LoadingButton startIcon={<AutorenewIcon/>} color={"secondary"}
                                                       variant={"contained"}
                                                       onClick={async () => await retrySubscriptionPayment()}>Retry</LoadingButton>
                                    </Grid>
                                    <Grid item>
                                        <Button startIcon={<CreditCardIcon/>} color={"primary"} variant={"contained"}
                                                onClick={async () => {
                                                    setInitialStep(0);
                                                    setOverrideCard(true);
                                                }
                                                }>Update card</Button>
                                    </Grid>
                                </Grid>
                            </>;
                        }
                    }
                };

                return (
                        <Box style={{margin: "2rem"}}>
                            {subscription &&
                                    <Grid container spacing={1} justifyContent={"center"} alignItems={"center"}>
                                        {body()}
                                    </Grid>
                            }
                        </Box>
                );
            }
        },
    ]


    const SubscriptionError = useMemo(() => {
        if (selectedCompanyStore.subscriptionStatus) {
            if (selectedCompanyStore.subscriptionStatus == ActiveSubscriptionStatus.DISABLED) {
                return <Grid item xs={12}>
                    <Typography variant="body2">Subscription is not active</Typography>
                </Grid>
            } else {
                return undefined
            }
        } else {
            return <Grid item xs={12}>
                <Typography variant="body2">No subscription info found</Typography>
            </Grid>
        }
    }, [selectedCompanyStore.subscriptionStatus])

    return <>
        <LoaderWithCompanySelected loading={!status}>
            <Grid container justifyContent={"center"}>
                <Grid item xs={12}>
                    {!!SubscriptionError && SubscriptionError}
                    {!SubscriptionError &&
                            <HorizontalStepper key={initialStep} steps={steps} initialStep={initialStep}/>}
                </Grid>
            </Grid>
        </LoaderWithCompanySelected>
    </>;
}));
